import * as Yup from "yup";
import { FC, useCallback, useEffect, useState } from "react";
import {
  ManifestDetailInterface,
} from "../../interfaces/ManifestInterface";
import { InventoryHeaderInterface } from "../../interfaces/InventoryInterface";
import classNames from "classnames";
import { FormDatePicker, FormTextInput } from "../../components/FormFields";
import moment from "moment";
import { PrimaryButton } from "../../components/Buttons";
import { useAppSelector } from "../../store/hooks";
import { alertService, loaderService } from "../../services";
import { getInventoryDetails, getInventoryHeaders } from "../../services/inventoryServices";
import { Formik } from "formik";
import InventoryDetailModal from "./InventoryDetailModal";
import InventoryHeaderTable from "./InventoryHeaderTable";

interface InventorySearchFromValues {
  inventoryNumber: string;
  toDate: Date | null;
  fromDate: Date | null;
}

const InventoryGeneratedTable: FC = () => {
  const loading = loaderService.useIsLoading();
  const user = useAppSelector((state) => state.user);

  const [openModal, setOpenModal] = useState(false);
  const [inventory, setInventory] = useState<InventoryHeaderInterface>();
  const [details, setDetails] = useState<ManifestDetailInterface[]>([]);
  const [generatedDispatchList, setGeneratedDispatchList] = useState<
    InventoryHeaderInterface[]
  >([]);
  const [initialSearchDone, setInitialSearchDone] = useState(false);

  const validationSchema = Yup.object().shape({
    toDate: Yup.date().required("La fecha inicial es requerida"),
    fromDate: Yup.date().required("La fecha final es requerida"),
  });

  const handleSearch = useCallback(async (values: InventorySearchFromValues) => {
    const buCode = user.businessUnit?.code;

    if (!buCode) {
      alertService.error(
        "No hay ninguna tienda asociada a la sesión del usuario."
      );
      return;
    }

    loaderService.start();
    // For generated inventories, we use status ID for completed/generated inventories
    // Assuming status ID 2 is for generated (adjust as needed)
    const inventories = await getInventoryHeaders(undefined, values.inventoryNumber || undefined, buCode, undefined);
    loaderService.stop();

    if (inventories === null || inventories.model === null || inventories.model.length === 0) {
      setGeneratedDispatchList([]);
      return;
    }

    setGeneratedDispatchList(inventories.model);
  }, [user.businessUnit?.code]);

  useEffect(() => {
    if (!initialSearchDone && user.businessUnit?.code) {
      const defaultValues: InventorySearchFromValues = {
        inventoryNumber: "",
        fromDate: moment().startOf('month').toDate(),
        toDate: new Date(),
      };
      handleSearch(defaultValues);
      setInitialSearchDone(true);
    }
  }, [user.businessUnit?.code, initialSearchDone, handleSearch]);

  const handleInventoryClick = async (inventory: InventoryHeaderInterface) => {
    const inventoryHeaderID = inventory.inventoryHeaderID;

    if (loading) {
      return;
    }
    if (!inventoryHeaderID) {
      alertService.error("Parece que el inventario no tiene un código asociado");
      return;
    }

    loaderService.start();
    const inventoryDetails = await getInventoryDetails(
      inventoryHeaderID
    );
    loaderService.stop();

    if (
      inventoryDetails === null ||
      !inventoryDetails ||
      !inventoryDetails.model
    ) {
      alertService.error("Hubo un error obteniendo los detalles del inventario");
      return;
    }

    const sorteredInventoryDetails = inventoryDetails.model.sort(
      (a, b) => {
        const aValue = a.lineSeqNumber ?? -Infinity;
        const bValue = b.lineSeqNumber ?? -Infinity;

        return bValue - aValue;
      }
    );

    setInventory(inventory);
    setDetails(sorteredInventoryDetails);
    setOpenModal(true);
  };

  return (
    <div className="flex flex-1 flex-col bg-white gap-4 rounded-lg border px-6 py-6 w-full">
      <h1 className="text-2xl font-semibold text-gray-800 truncate mb-2">
        Inventarios
      </h1>
      <div
        className={classNames("flex flex-1 flex-col bg-white gap-4 rounded-lg")}
      >
        <Formik<InventorySearchFromValues>
          initialValues={{
            inventoryNumber: "",
            toDate: new Date(),
            fromDate: moment().subtract(3, "days").toDate(),
          }}
          validationSchema={validationSchema}
          onSubmit={handleSearch}
        >
          {(formik) => (
            <form
              className="flex flex-row gap-4 mb-2 w-full"
              onSubmit={formik.handleSubmit}
            >
              <div className="w-full sm:w-1/3">
                <FormTextInput
                  id="inventoryNumber"
                  name="inventoryNumber"
                  value={formik.values.inventoryNumber}
                  onChange={(e) =>
                    formik.setFieldValue(
                      "inventoryNumber",
                      e.target.value.toUpperCase()
                    )
                  }
                  label="Código de inventario"
                  placeholder="Ingrese código de inventario"
                />
              </div>

              <div className="w-full sm:w-1/3">
                <FormDatePicker
                  id="fromDate"
                  name="fromDate"
                  label="Desde"
                  error={formik.errors.fromDate}
                  useRange={false}
                  maxDate={new Date()}
                  value={{
                    startDate: formik.values.fromDate,
                    endDate: formik.values.fromDate,
                  }}
                  toggleClassName={(oldClassname) =>
                    classNames(oldClassname, "text-indigo-600")
                  }
                  onChange={(e) =>
                    formik.setFieldValue(
                      "fromDate",
                      !!e?.startDate ? moment(e.startDate).toDate() : null
                    )
                  }
                  configs={{
                    shortcuts: {},
                  }}
                />
              </div>
              <div className="w-full sm:w-1/3">
                <FormDatePicker
                  id="toDate"
                  name="toDate"
                  label="Hasta"
                  error={formik.errors.toDate}
                  useRange={false}
                  maxDate={new Date()}
                  value={{
                    startDate: formik.values.toDate,
                    endDate: formik.values.toDate,
                  }}
                  toggleClassName={(oldClassname) =>
                    classNames(oldClassname, "text-indigo-600")
                  }
                  onChange={(e) =>
                    formik.setFieldValue(
                      "toDate",
                      !!e?.startDate ? moment(e.startDate).toDate() : null
                    )
                  }
                  configs={{
                    shortcuts: {},
                  }}
                />
              </div>

              <div className="w-1/12 mt-8">
                <PrimaryButton className="!px-6 !py-2" disabled={loading}>
                  Buscar
                </PrimaryButton>
              </div>
            </form>
          )}
        </Formik>

        <InventoryHeaderTable
          inventorys={generatedDispatchList}
          onInventoryClick={handleInventoryClick}
        />
      </div>
      {inventory && (
        <InventoryDetailModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          inventory={inventory}
          detailData={details}
          showPiece = {true}
        />
      )}
    </div>
  );
};

export default InventoryGeneratedTable;
